import React, { useState, useEffect } from 'react';

import { isEqual, cloneDeep, isEmpty, head } from 'lodash';
import PropTypes from 'prop-types';

import { CardComponent } from 'components/CardComponent/CardComponent';
import { fontSettingsProp } from 'components/propTypesConsts';
import { SettingsForm } from 'components/SettingsForm/SettingsForm';
import { useSubmittingState } from 'hooks';
import { getFontSettingsWithNums, getFontSettingsWithPx, getTWithPathKey } from 'utils';

import { BUTTON_SHAPES, ECOMMERCE_OVERLAYS_SETTINGS_TYPES, OVERLAY_CONFIGURATION } from '../constants';
import { ProductOverlayPreview } from '../ProductOverlalyPreview/ProductOverlayPreview';
import { ShoppingCartPreview } from '../ShoppingCartPreview/ShoppingCartPreview';
import { SignInOverlayPreview } from '../SignInOverlayPreview/SignInOverlayPreview';

import { EcommerceOverlaySettings } from './EcommerceOverlaySettings';

export function EcommerceOverlaySettingsForm({
  isLanguageCodeVisible,
  pageLanguageTabValue,
  overlaySettings,
  renderHeaderNav,
  onSave,
  enabled,
  overlayType,
}) {
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();
  const FORM_ID = `sphere-ecommerce-overlay-${overlayType}`;

  useEffect(() => {
    if (!isEmpty(overlaySettings)) updateSettings(overlaySettings);
  }, [overlaySettings]);

  const getT = getTWithPathKey(`components.ecommerce.overlay.${overlayType}`);

  const updateSettings = newSettings => {
    if (isEmpty(newSettings)) return;

    const currentSettings = {
      ...newSettings,
      ecommerceFonts: newSettings.ecommerceFonts?.map(getFontSettingsWithNums),
      ecommerceButtons: newSettings.ecommerceButtons?.map(button => ({
        ...button,
        color: { hex: button.color },
        colorDisabled: { hex: button.colorDisabled },
        borderColor: { hex: button.borderColor },
        borderColorDisabled: { hex: button.borderColorDisabled },
      })),
      backgroundColor: { hex: newSettings.backgroundColor },
      secondaryColor: { hex: newSettings.secondaryColor },
    };

    if (OVERLAY_CONFIGURATION[overlayType].withTextFields) {
      currentSettings.ecommerceTextField = {
        ...head(newSettings.ecommerceTextFields),
        borderColor: { hex: head(newSettings.ecommerceTextFields).borderColor },
        borderWidth: head(newSettings.ecommerceTextFields).borderWidth,
      };
    }

    if (OVERLAY_CONFIGURATION[overlayType].withControlColor) {
      currentSettings.ecommerceCustomColors = [
        {
          ...head(newSettings.ecommerceCustomColors),
          color: { hex: head(newSettings.ecommerceCustomColors).color },
        },
      ];
    }

    setInitialSettings(currentSettings);
    setSettings(cloneDeep(currentSettings));
  };

  const handleSaveSettings = withHandlingSubmittingState(async ({ target: { id } }) => {
    if (id !== FORM_ID) return;

    const settingsToSaving = {
      ...settings,
      ecommerceFonts: settings.ecommerceFonts.map(getFontSettingsWithPx),
      ecommerceButtons: settings.ecommerceButtons?.map(button => ({
        ...button,
        color: button.color.hex,
        colorDisabled: button.colorDisabled.hex,
        borderColor: button.borderColor.hex,
        borderColorDisabled: button.borderColorDisabled.hex,
      })),
      backgroundColor: settings.backgroundColor.hex,
      secondaryColor: settings.secondaryColor.hex,
    };

    if (OVERLAY_CONFIGURATION[overlayType].withTextFields) {
      settingsToSaving.ecommerceTextFields = [
        {
          ...settings.ecommerceTextField,
          borderColor: settings.ecommerceTextField.borderColor.hex,
        },
      ];
    }

    if (OVERLAY_CONFIGURATION[overlayType].withControlColor) {
      settingsToSaving.ecommerceCustomColors = [
        {
          ...head(settings.ecommerceCustomColors),
          color: head(settings.ecommerceCustomColors).color.hex,
        },
      ];
    }

    await onSave(settingsToSaving);
  });

  const getPreview = () => {
    switch (overlayType) {
      case ECOMMERCE_OVERLAYS_SETTINGS_TYPES.pdp:
        return ProductOverlayPreview;

      case ECOMMERCE_OVERLAYS_SETTINGS_TYPES.signIn:
        return SignInOverlayPreview;

      case ECOMMERCE_OVERLAYS_SETTINGS_TYPES.shoppingCart:
        return ShoppingCartPreview;

      default:
        return () => null;
    }
  };

  return (
    <SettingsForm
      isSaveButtonVisible={!isSubmitting && !isEqual(initialSettings, settings)}
      handleSave={handleSaveSettings}
      id={FORM_ID}
    >
      <CardComponent title={getT('title')} isLoading={isSubmitting} CardHeaderNav={renderHeaderNav} sm={8}>
        {enabled && !isEmpty(settings) && (
          <EcommerceOverlaySettings
            settings={settings}
            setSettings={setSettings}
            pageLanguageTabValue={pageLanguageTabValue}
            isLanguageCodeVisible={isLanguageCodeVisible}
            Preview={getPreview()}
          />
        )}
      </CardComponent>
    </SettingsForm>
  );
}

EcommerceOverlaySettingsForm.propTypes = {
  isLanguageCodeVisible: PropTypes.bool,
  pageLanguageTabValue: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  overlaySettings: PropTypes.shape({
    ecommerceFonts: PropTypes.arrayOf(fontSettingsProp),
    ecommerceButtons: PropTypes.array,
    backgroundColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    buttonShape: PropTypes.oneOf(Object.values(BUTTON_SHAPES)),
    id: PropTypes.number,
  }),
  renderHeaderNav: PropTypes.func,
  enabled: PropTypes.bool,
  overlayType: PropTypes.string.isRequired,
};

EcommerceOverlaySettingsForm.defaultProps = {
  renderHeaderNav: () => null,
  enabled: true,
  overlaySettings: {},
  isLanguageCodeVisible: false,
};
